import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Edit, Plus, DollarSign, TrendingUp, Link as LinkIcon, Copy, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { TransactionForm } from "./TransactionForm";
import { LoanNoteDetails } from "./LoanNoteDetails";
import { nanoid } from "nanoid";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Transaction {
  id: string;
  transaction_date: string;
  amount: number;
  transaction_type: string;
  notes: string | null;
}

interface LenderProfileDetailProps {
  lender: any;
  onEdit: () => void;
  onClose: () => void;
}

export const LenderProfileDetail = ({
  lender,
  onEdit,
  onClose,
}: LenderProfileDetailProps) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accruedInterest, setAccruedInterest] = useState(0);
  const [totalPrincipal, setTotalPrincipal] = useState(0);
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [accessEmail, setAccessEmail] = useState("");
  const [expirationDays, setExpirationDays] = useState(90);
  const [generatedLink, setGeneratedLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchTransactions();
  }, [lender.id]);

  const fetchTransactions = async () => {
    try {
      const { data, error } = await supabase
        .from("lender_transactions")
        .select("*")
        .eq("lender_profile_id", lender.id)
        .order("transaction_date", { ascending: false });

      if (error) throw error;
      setTransactions(data || []);
      calculateInterest(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateInterest = (txns: Transaction[]) => {
    // Calculate total principal (sum of loan_received)
    const principal = txns
      .filter((t) => t.transaction_type === "loan_received")
      .reduce((sum, t) => sum + Number(t.amount), 0);

    // Find the last repayment date
    const lastRepayment = txns
      .filter((t) =>
        ["interest_payment", "principal_repayment"].includes(t.transaction_type)
      )
      .sort((a, b) =>
        new Date(b.transaction_date).getTime() -
        new Date(a.transaction_date).getTime()
      )[0];

    // Calculate days since last repayment (or loan start)
    const startDate = lastRepayment
      ? new Date(lastRepayment.transaction_date)
      : new Date(lender.loan_start_date);
    const today = new Date();
    const daysDiff = Math.floor(
      (today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    // Daily interest = principal × (annual rate / 365)
    const dailyRate = Number(lender.agreed_interest_rate) / 100 / 365;
    const interest = principal * dailyRate * daysDiff;

    setTotalPrincipal(principal);
    setAccruedInterest(interest);
  };

  const getTransactionTypeLabel = (type: string) => {
    switch (type) {
      case "loan_received":
        return "Loan Received";
      case "interest_payment":
        return "Interest Payment";
      case "principal_repayment":
        return "Principal Repayment";
      default:
        return type;
    }
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case "loan_received":
        return "bg-green-500/10 text-green-700 dark:text-green-400";
      case "interest_payment":
        return "bg-blue-500/10 text-blue-700 dark:text-blue-400";
      case "principal_repayment":
        return "bg-purple-500/10 text-purple-700 dark:text-purple-400";
      default:
        return "bg-gray-500/10 text-gray-700 dark:text-gray-400";
    }
  };

  const generateAccessLink = async () => {
    if (!accessEmail) {
      toast({
        title: "Error",
        description: "Please enter an email address",
        variant: "destructive",
      });
      return;
    }

    try {
      const token = nanoid(32);
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + expirationDays);

      const { error } = await supabase
        .from("lender_dashboard_access")
        .insert({
          email: accessEmail,
          lender_profile_id: lender.id,
          access_token: token,
          expires_at: expiresAt.toISOString(),
        });

      if (error) throw error;

      const link = `${window.location.origin}/lender-dashboard?token=${token}`;
      setGeneratedLink(link);
      toast({
        title: "Success",
        description: "Access link generated successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    toast({
      title: "Success",
      description: "Link copied to clipboard",
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const resetLinkDialog = () => {
    setAccessEmail("");
    setExpirationDays(90);
    setGeneratedLink("");
    setCopied(false);
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {/* <div className="grid gap-4 md:grid-cols-3">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <DollarSign className="h-4 w-4" />
            <span>Total Principal</span>
          </div>
          <p className="text-2xl font-bold">
            ${totalPrincipal.toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </p>
        </Card>

        <Card className="p-4 bg-primary/5">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <TrendingUp className="h-4 w-4" />
            <span>Accrued Interest</span>
          </div>
          <p className="text-2xl font-bold text-primary">
            ${accruedInterest.toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            at {lender.agreed_interest_rate}% annual rate
          </p>
        </Card>

        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Payment Schedule</div>
          <p className="text-lg font-semibold capitalize">
            {lender.payment_schedule}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Started: {new Date(lender.loan_start_date).toLocaleDateString()}
          </p>
        </Card>
      </div> */}

      {/* Tabs */}
      <Tabs defaultValue="loan-note" className="w-full">
        {/* <TabsList>
          <TabsTrigger value="loan-note">Loan Note</TabsTrigger>
        </TabsList> */}

        <TabsContent value="profile" className="mt-4">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Lender Information</h3>
              <Button onClick={onEdit} variant="outline" size="sm">
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {lender.legal_contact && (
                <div>
                  <Label className="text-muted-foreground">Legal Contact</Label>
                  <p className="font-medium">{lender.legal_contact}</p>
                </div>
              )}
              {lender.legal_contact_phone && (
                <div>
                  <Label className="text-muted-foreground">Phone</Label>
                  <p className="font-medium">{lender.legal_contact_phone}</p>
                </div>
              )}
              {lender.legal_contact_email && (
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="font-medium">{lender.legal_contact_email}</p>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="loan-note" className="mt-4">
          <LoanNoteDetails lenderProfileId={lender.id} />
        </TabsContent>

        <TabsContent value="transactions" className="mt-4">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Transaction History</h3>
              <Button onClick={() => setShowTransactionForm(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Transaction
              </Button>
            </div>
            {loading ? (
              <p className="text-muted-foreground">Loading...</p>
            ) : transactions.length === 0 ? (
              <Card className="p-6 text-center text-muted-foreground">
                No transactions yet
              </Card>
            ) : (
              <div className="space-y-2">
                {transactions.map((txn) => (
                  <Card key={txn.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge className={getTransactionTypeColor(txn.transaction_type)}>
                            {getTransactionTypeLabel(txn.transaction_type)}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            {new Date(txn.transaction_date).toLocaleDateString()}
                          </span>
                        </div>
                        {txn.notes && (
                          <p className="text-sm text-muted-foreground">{txn.notes}</p>
                        )}
                      </div>
                      <p className="text-lg font-semibold">
                        ${Math.abs(Number(txn.amount)).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                        })}
                      </p>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="access" className="mt-4">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Generate Dashboard Access Link</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="accessEmail">Email Address</Label>
                <Input
                  id="accessEmail"
                  type="email"
                  placeholder="lender@example.com"
                  value={accessEmail}
                  onChange={(e) => setAccessEmail(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="expirationDays">Expiration (days)</Label>
                <Input
                  id="expirationDays"
                  type="number"
                  value={expirationDays}
                  onChange={(e) => setExpirationDays(parseInt(e.target.value) || 90)}
                />
              </div>
              <Button onClick={generateAccessLink} className="w-full">
                <LinkIcon className="w-4 h-4 mr-2" />
                Generate Access Link
              </Button>

              {generatedLink && (
                <div className="p-4 bg-muted rounded-lg space-y-2">
                  <Label>Generated Link</Label>
                  <div className="flex gap-2">
                    <Input
                      value={generatedLink}
                      readOnly
                      className="font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={copyToClipboard}
                    >
                      {copied ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    This link will expire in {expirationDays} days
                  </p>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={showTransactionForm} onOpenChange={setShowTransactionForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Transaction</DialogTitle>
          </DialogHeader>
          <TransactionForm
            lenderId={lender.id}
            onSuccess={() => {
              setShowTransactionForm(false);
              fetchTransactions();
            }}
            onCancel={() => setShowTransactionForm(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};
